import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { FloatingCTA } from '../components/FloatingCTA';
import { CheckIcon, XIcon, StarIcon, ArrowRightIcon, ShieldIcon, FileTextIcon, MessageCircleIcon, BookOpenIcon, ClockIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
export function PremiumOffersPage() {
  // Pricing plan data
  const plans = [{
    name: 'Essentiel',
    price: '149',
    period: 'par mois',
    description: "L'essentiel pour démarrer avec l'assistance premium",
    features: ['Revue de dossier (2 par mois)', 'Assistance par email sous 24h', 'Accès à la base de connaissances', 'Support téléphonique 5j/7'],
    notIncluded: ['Assistance en temps réel', 'Conseils stratégiques personnalisés', 'Assistance juridique spécialisée', 'Gestionnaire de compte dédié'],
    cta: 'Choisir ce plan',
    popular: false,
    color: 'blue'
  }, {
    name: 'Business',
    price: '299',
    period: 'par mois',
    description: 'Notre offre la plus populaire pour les entreprises',
    features: ['Revue de dossier (5 par mois)', 'Assistance par email sous 12h', 'Support téléphonique 6j/7', 'Assistance en temps réel par chat', '1 session de conseil stratégique par mois', 'Accès prioritaire aux experts'],
    notIncluded: ['Assistance juridique spécialisée', 'Gestionnaire de compte dédié'],
    cta: 'Choisir ce plan',
    popular: true,
    color: 'yellow'
  }, {
    name: 'Premium',
    price: '499',
    period: 'par mois',
    description: 'Accompagnement complet pour maximiser vos chances',
    features: ['Revue de dossier illimitée', 'Assistance par email sous 4h', 'Support téléphonique 7j/7', 'Assistance en temps réel prioritaire', 'Sessions de conseil stratégique illimitées', 'Assistance juridique spécialisée', 'Gestionnaire de compte dédié', 'Analyse concurrentielle personnalisée'],
    notIncluded: [],
    cta: 'Choisir ce plan',
    popular: false,
    color: 'green'
  }];
  // Feature comparison data
  const featureComparison = [{
    category: 'Revue de dossier',
    features: [{
      name: 'Nombre de revues mensuelles',
      essentiel: '2',
      business: '5',
      premium: 'Illimité'
    }, {
      name: 'Délai de retour',
      essentiel: '48h',
      business: '24h',
      premium: '12h'
    }, {
      name: 'Vérification administrative',
      essentiel: true,
      business: true,
      premium: true
    }, {
      name: 'Analyse technique approfondie',
      essentiel: false,
      business: true,
      premium: true
    }, {
      name: "Optimisation de l'offre financière",
      essentiel: false,
      business: true,
      premium: true
    }]
  }, {
    category: 'Assistance',
    features: [{
      name: 'Support par email',
      essentiel: '24h',
      business: '12h',
      premium: '4h'
    }, {
      name: 'Support téléphonique',
      essentiel: '5j/7',
      business: '6j/7',
      premium: '7j/7'
    }, {
      name: 'Assistance en temps réel par chat',
      essentiel: false,
      business: true,
      premium: true
    }, {
      name: 'Accès prioritaire aux experts',
      essentiel: false,
      business: true,
      premium: true
    }, {
      name: 'Gestionnaire de compte dédié',
      essentiel: false,
      business: false,
      premium: true
    }]
  }, {
    category: 'Conseil stratégique',
    features: [{
      name: 'Sessions de conseil stratégique',
      essentiel: 'Aucune',
      business: '1 par mois',
      premium: 'Illimité'
    }, {
      name: 'Analyse de positionnement',
      essentiel: false,
      business: true,
      premium: true
    }, {
      name: 'Stratégie de différenciation',
      essentiel: false,
      business: true,
      premium: true
    }, {
      name: 'Analyse concurrentielle',
      essentiel: false,
      business: false,
      premium: true
    }]
  }, {
    category: 'Assistance juridique',
    features: [{
      name: 'Vérification de conformité légale',
      essentiel: false,
      business: false,
      premium: true
    }, {
      name: 'Conseils sur clauses contractuelles',
      essentiel: false,
      business: false,
      premium: true
    }, {
      name: 'Assistance en cas de litige',
      essentiel: false,
      business: false,
      premium: true
    }]
  }];
  return <div className="min-h-screen bg-white text-gray-900 font-sans" data-id="element-2857">
      <Navbar data-id="element-2858" />
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-blue-50 to-white relative overflow-hidden" data-id="element-2859">
        <div className="container mx-auto px-4 relative z-10" data-id="element-2860">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2861">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-2862">
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full mb-4" data-id="element-2863">
                Nos offres
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6" data-id="element-2864">
                Des offres adaptées à vos besoins
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-2865">
                Choisissez la formule d'assistance Premium qui correspond le
                mieux à vos objectifs et à votre budget. Tous nos plans incluent
                un accompagnement personnalisé par nos experts.
              </p>
            </motion.div>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-radial from-yellow-100 to-transparent opacity-70" data-id="element-2866"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-radial from-blue-50 to-transparent opacity-70" data-id="element-2867"></div>
      </section>
      {/* Pricing Section */}
      <section className="py-16" data-id="element-2868">
        <div className="container mx-auto px-4" data-id="element-2869">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2870">
            {plans.map((plan, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className={`bg-white rounded-xl shadow-lg border ${plan.popular ? 'border-yellow-400 ring-2 ring-yellow-400 transform md:-translate-y-4' : 'border-gray-200'} overflow-hidden`} data-id="element-2871">
                {plan.popular && <div className="bg-yellow-400 text-blue-900 text-center py-2 font-medium" data-id="element-2872">
                    Recommandé
                  </div>}
                <div className="p-6" data-id="element-2873">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2874">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-2875">{plan.description}</p>
                  <div className="mb-6" data-id="element-2876">
                    <span className="text-4xl font-bold text-blue-900" data-id="element-2877">
                      {plan.price}€
                    </span>
                    <span className="text-gray-500 ml-1" data-id="element-2878">{plan.period}</span>
                  </div>
                  <div className="space-y-4 mb-8" data-id="element-2879">
                    {plan.features.map((feature, idx) => <div key={idx} className="flex items-start" data-id="element-2880">
                        <CheckIcon className="text-green-500 mt-1 mr-3 flex-shrink-0" size={18} data-id="element-2881" />
                        <span data-id="element-2882">{feature}</span>
                      </div>)}
                    {plan.notIncluded.map((feature, idx) => <div key={idx} className="flex items-start text-gray-400" data-id="element-2883">
                        <XIcon className="text-gray-300 mt-1 mr-3 flex-shrink-0" size={18} data-id="element-2884" />
                        <span data-id="element-2885">{feature}</span>
                      </div>)}
                  </div>
                  <Button variant={plan.popular ? 'primary' : 'outline'} fullWidth className={plan.popular ? 'bg-yellow-500 hover:bg-yellow-600 text-blue-900 shadow-lg' : 'border-2 border-blue-900'} data-id="element-2886">
                    {plan.cta}
                  </Button>
                </div>
              </motion.div>)}
          </div>
          <div className="mt-12 text-center" data-id="element-2887">
            <p className="text-gray-600 mb-4" data-id="element-2888">
              Besoin d'une solution sur mesure pour votre entreprise ?
            </p>
            <Link to="/parler-conseiller" data-id="element-2889">
              <Button variant="secondary" size="lg" className="shadow-md" data-id="element-2890">
                Contactez notre équipe commerciale
              </Button>
            </Link>
          </div>
        </div>
      </section>
      {/* Feature Comparison Table */}
      <section className="py-16 bg-gray-50" data-id="element-2891">
        <div className="container mx-auto px-4" data-id="element-2892">
          <div className="text-center mb-12" data-id="element-2893">
            <h2 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-2894">
              Comparaison des fonctionnalités
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-2895">
              Découvrez en détail les fonctionnalités incluses dans chaque
              formule pour choisir celle qui correspond le mieux à vos besoins.
            </p>
          </div>
          <div className="overflow-x-auto" data-id="element-2896">
            <table className="w-full bg-white shadow-lg rounded-lg overflow-hidden" data-id="element-2897">
              <thead data-id="element-2898">
                <tr className="bg-blue-900 text-white" data-id="element-2899">
                  <th className="py-4 px-6 text-left min-w-[250px]" data-id="element-2900">
                    Fonctionnalités
                  </th>
                  <th className="py-4 px-6 text-center" data-id="element-2901">Essentiel</th>
                  <th className="py-4 px-6 text-center bg-yellow-400 text-blue-900" data-id="element-2902">
                    Business
                  </th>
                  <th className="py-4 px-6 text-center" data-id="element-2903">Premium</th>
                </tr>
              </thead>
              <tbody data-id="element-2904">
                {featureComparison.map((category, categoryIndex) => <Fragment key={categoryIndex} data-id="element-2905">
                    <tr className="bg-blue-50" data-id="element-2906">
                      <td colSpan={4} className="py-3 px-6 font-semibold text-blue-800" data-id="element-2907">
                        {category.category}
                      </td>
                    </tr>
                    {category.features.map((feature, featureIndex) => <tr key={`${categoryIndex}-${featureIndex}`} className={featureIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-2908">
                        <td className="py-3 px-6 border-t border-gray-200" data-id="element-2909">
                          {feature.name}
                        </td>
                        <td className="py-3 px-6 text-center border-t border-gray-200" data-id="element-2910">
                          {typeof feature.essentiel === 'boolean' ? feature.essentiel ? <CheckIcon className="mx-auto text-green-500" size={18} data-id="element-2911" /> : <XIcon className="mx-auto text-gray-300" size={18} data-id="element-2912" /> : feature.essentiel}
                        </td>
                        <td className="py-3 px-6 text-center border-t border-gray-200 bg-yellow-50" data-id="element-2913">
                          {typeof feature.business === 'boolean' ? feature.business ? <CheckIcon className="mx-auto text-green-500" size={18} data-id="element-2914" /> : <XIcon className="mx-auto text-gray-300" size={18} data-id="element-2915" /> : feature.business}
                        </td>
                        <td className="py-3 px-6 text-center border-t border-gray-200" data-id="element-2916">
                          {typeof feature.premium === 'boolean' ? feature.premium ? <CheckIcon className="mx-auto text-green-500" size={18} data-id="element-2917" /> : <XIcon className="mx-auto text-gray-300" size={18} data-id="element-2918" /> : feature.premium}
                        </td>
                      </tr>)}
                  </Fragment>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      {/* Services Overview */}
      <section className="py-16" data-id="element-2919">
        <div className="container mx-auto px-4" data-id="element-2920">
          <div className="text-center mb-12" data-id="element-2921">
            <h2 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-2922">
              Nos services d'assistance Premium
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-2923">
              Découvrez en détail les services inclus dans nos offres
              d'assistance Premium
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2924">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} viewport={{
            once: true
          }} className="bg-white p-6 rounded-xl shadow-md border border-gray-200" data-id="element-2925">
              <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-5" data-id="element-2926">
                <FileTextIcon size={32} data-id="element-2927" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2928">
                Revue de dossier
              </h3>
              <p className="text-gray-600 mb-5" data-id="element-2929">
                Nos experts examinent en détail votre dossier de candidature
                pour s'assurer qu'il répond à toutes les exigences et maximise
                vos chances de succès.
              </p>
              <div className="space-y-3" data-id="element-2930">
                <div className="flex items-center" data-id="element-2931">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2932" />
                  <span className="text-gray-700" data-id="element-2933">
                    Vérification de la conformité administrative
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2934">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2935" />
                  <span className="text-gray-700" data-id="element-2936">
                    Analyse de la partie technique
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2937">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2938" />
                  <span className="text-gray-700" data-id="element-2939">
                    Optimisation de l'offre financière
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2940">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2941" />
                  <span className="text-gray-700" data-id="element-2942">
                    Recommandations d'amélioration
                  </span>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} viewport={{
            once: true
          }} className="bg-white p-6 rounded-xl shadow-md border border-gray-200" data-id="element-2943">
              <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-5" data-id="element-2944">
                <MessageCircleIcon size={32} data-id="element-2945" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2946">
                Assistance en temps réel
              </h3>
              <p className="text-gray-600 mb-5" data-id="element-2947">
                Bénéficiez d'un support immédiat de nos experts pour répondre à
                vos questions urgentes et résoudre vos problèmes.
              </p>
              <div className="space-y-3" data-id="element-2948">
                <div className="flex items-center" data-id="element-2949">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2950" />
                  <span className="text-gray-700" data-id="element-2951">
                    Support par chat, email et téléphone
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2952">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2953" />
                  <span className="text-gray-700" data-id="element-2954">
                    Temps de réponse garanti selon votre formule
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2955">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2956" />
                  <span className="text-gray-700" data-id="element-2957">
                    Accès prioritaire à nos experts
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2958">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2959" />
                  <span className="text-gray-700" data-id="element-2960">
                    Suivi personnalisé de vos demandes
                  </span>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} viewport={{
            once: true
          }} className="bg-white p-6 rounded-xl shadow-md border border-gray-200" data-id="element-2961">
              <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-5" data-id="element-2962">
                <BookOpenIcon size={32} data-id="element-2963" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2964">
                Conseil stratégique
              </h3>
              <p className="text-gray-600 mb-5" data-id="element-2965">
                Recevez des recommandations personnalisées pour améliorer votre
                stratégie de réponse aux appels d'offres.
              </p>
              <div className="space-y-3" data-id="element-2966">
                <div className="flex items-center" data-id="element-2967">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2968" />
                  <span className="text-gray-700" data-id="element-2969">
                    Analyse de votre positionnement
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2970">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2971" />
                  <span className="text-gray-700" data-id="element-2972">
                    Identification des avantages concurrentiels
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2973">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2974" />
                  <span className="text-gray-700" data-id="element-2975">
                    Recommandations sur les points à mettre en avant
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2976">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2977" />
                  <span className="text-gray-700" data-id="element-2978">
                    Stratégie de différenciation
                  </span>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} viewport={{
            once: true
          }} className="bg-white p-6 rounded-xl shadow-md border border-gray-200" data-id="element-2979">
              <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-5" data-id="element-2980">
                <ShieldIcon size={32} data-id="element-2981" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2982">
                Assistance juridique
              </h3>
              <p className="text-gray-600 mb-5" data-id="element-2983">
                Nos juristes spécialisés vous aident à naviguer dans les
                complexités légales et réglementaires des marchés publics.
              </p>
              <div className="space-y-3" data-id="element-2984">
                <div className="flex items-center" data-id="element-2985">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2986" />
                  <span className="text-gray-700" data-id="element-2987">
                    Vérification de la conformité légale
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2988">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2989" />
                  <span className="text-gray-700" data-id="element-2990">
                    Conseils sur les clauses contractuelles
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2991">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2992" />
                  <span className="text-gray-700" data-id="element-2993">
                    Assistance en cas de litige
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2994">
                  <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0" data-id="element-2995" />
                  <span className="text-gray-700" data-id="element-2996">
                    Veille réglementaire personnalisée
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Testimonials */}
      <section className="py-16 bg-gray-50" data-id="element-2997">
        <div className="container mx-auto px-4" data-id="element-2998">
          <div className="text-center mb-12" data-id="element-2999">
            <h2 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-3000">
              Ils ont choisi notre assistance Premium
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-3001">
              Découvrez les témoignages de nos clients qui ont amélioré leur
              taux de succès grâce à notre accompagnement.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3002">
            {[{
            quote: "Depuis que nous avons souscrit à l'offre Business, notre taux de succès dans les appels d'offres a augmenté de 35%. L'assistance en temps réel nous a permis de résoudre rapidement des problèmes critiques.",
            author: 'Mohammed El Amrani',
            position: 'Directeur Général, BuildTech Maroc',
            image: 'https://randomuser.me/api/portraits/men/32.jpg',
            plan: 'Business'
          }, {
            quote: "La revue de dossier a été un élément décisif dans notre succès. Les experts ont identifié des points d'amélioration que nous n'aurions jamais remarqués, ce qui nous a permis de remporter un marché important.",
            author: 'Fatima Bensouda',
            position: "Responsable Appels d'Offres, MedEquip",
            image: 'https://randomuser.me/api/portraits/women/44.jpg',
            plan: 'Premium'
          }, {
            quote: "Même avec l'offre Essentiel, nous avons bénéficié d'un accompagnement de qualité. Les conseils reçus nous ont aidés à mieux structurer nos réponses et à être plus compétitifs.",
            author: 'Youssef Kaddouri',
            position: 'PDG, InfoSystems Tunisie',
            image: 'https://randomuser.me/api/portraits/men/75.jpg',
            plan: 'Essentiel'
          }].map((testimonial, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="bg-white p-6 rounded-xl shadow-md border border-gray-200" data-id="element-3003">
                <div className="flex items-center mb-4" data-id="element-3004">
                  <div className="h-8 w-8 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mr-2" data-id="element-3005">
                    <StarIcon size={16} data-id="element-3006" />
                  </div>
                  <span className="text-sm font-medium text-blue-800" data-id="element-3007">
                    Plan {testimonial.plan}
                  </span>
                </div>
                <p className="text-gray-700 mb-6 italic" data-id="element-3008">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center" data-id="element-3009">
                  <img src={testimonial.image} alt={testimonial.author} className="w-12 h-12 rounded-full mr-4" data-id="element-3010" />
                  <div data-id="element-3011">
                    <h4 className="font-bold text-gray-900" data-id="element-3012">
                      {testimonial.author}
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-3013">
                      {testimonial.position}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16" data-id="element-3014">
        <div className="container mx-auto px-4" data-id="element-3015">
          <div className="text-center mb-12" data-id="element-3016">
            <h2 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-3017">
              Questions fréquentes
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-3018">
              Nous avons rassemblé les questions les plus fréquentes concernant
              nos offres d'assistance Premium.
            </p>
          </div>
          <div className="max-w-3xl mx-auto" data-id="element-3019">
            <div className="space-y-6" data-id="element-3020">
              {[{
              question: 'Puis-je changer de formule à tout moment ?',
              answer: 'Oui, vous pouvez passer à une formule supérieure à tout moment. Le changement prend effet immédiatement et la différence de prix est calculée au prorata. Pour passer à une formule inférieure, le changement prendra effet à la fin de votre période de facturation en cours.'
            }, {
              question: 'Y a-t-il un engagement de durée ?',
              answer: "Nos formules sont disponibles avec ou sans engagement. Sans engagement, vous payez mois par mois et pouvez résilier à tout moment. Avec un engagement annuel, vous bénéficiez de 2 mois gratuits (soit 16% d'économie)."
            }, {
              question: 'Comment fonctionne la revue de dossier ?',
              answer: "Vous soumettez votre dossier via notre plateforme sécurisée, nos experts l'examinent selon une checklist complète et vous fournissent un rapport détaillé avec des recommandations d'amélioration dans les délais prévus par votre formule."
            }, {
              question: "Puis-je demander une assistance pour plusieurs appels d'offres simultanément ?",
              answer: "Oui, vous pouvez soumettre plusieurs demandes d'assistance simultanément, dans la limite du nombre de revues incluses dans votre formule. Avec la formule Premium, vous bénéficiez d'un nombre illimité de revues."
            }, {
              question: "Les sessions de conseil stratégique peuvent-elles être reportées d'un mois à l'autre ?",
              answer: "Pour la formule Business, si vous ne consommez pas votre session mensuelle, elle peut être reportée sur le mois suivant uniquement (maximum 2 sessions cumulables). Avec la formule Premium, vous bénéficiez d'un nombre illimité de sessions."
            }].map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: index * 0.1
            }} viewport={{
              once: true
            }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-3021">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3022">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600" data-id="element-3023">{faq.answer}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white" data-id="element-3024">
        <div className="container mx-auto px-4" data-id="element-3025">
          <div className="max-w-4xl mx-auto text-center" data-id="element-3026">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-3027">
              Prêt à maximiser vos chances de succès ?
            </h2>
            <p className="text-xl text-blue-100 mb-8" data-id="element-3028">
              Choisissez la formule qui correspond à vos besoins et commencez à
              bénéficier de notre expertise dès aujourd'hui.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-3029">
              <Button variant="primary" size="lg" className="shadow-lg shadow-yellow-500/20 bg-yellow-500 hover:bg-yellow-400 text-blue-900" data-id="element-3030">
                Commencer avec un essai gratuit
              </Button>
              <Link to="/parler-conseiller" data-id="element-3031">
                <Button variant="secondary" size="lg" className="bg-white text-blue-900 hover:bg-blue-50" data-id="element-3032">
                  Parler à un conseiller
                </Button>
              </Link>
            </div>
            <p className="text-blue-200 mt-4 text-sm" data-id="element-3033">
              Essai gratuit de 7 jours, sans engagement. Annulation facile.
            </p>
          </div>
        </div>
      </section>
      <Footer data-id="element-3034" />
      <FloatingCTA data-id="element-3035" />
    </div>;
}